import React, { useEffect, useState } from 'react';
import {
  Search, Plus, Pencil, Trash2, Eye, XCircle, CheckCircle, Plane, Calendar, DollarSign,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  getBookings, addBooking, updateBooking, deleteBooking,
  cancelBooking, confirmBooking, generateBookingRef, PROFIT_RATE,
} from '../firebase/firestore';
import AdminModal, { ModalFooter, FormInput, FormSelect } from '../components/AdminModal';

const EMPTY_BOOKING = {
  bookingRef: '',
  type: 'flight',
  status: 'confirmed',
  title: '',
  subtitle: '',
  userId: '',
  totalPrice: '',
  bookedAt: new Date().toISOString().slice(0, 16),
  paymentMethod: 'card',
};

export default function Bookings() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY_BOOKING);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    getBookings()
      .then(d => { setBookings(d); setLoading(false); })
      .catch(e => { toast.error(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  const openAdd = () => {
    setForm({ ...EMPTY_BOOKING, bookingRef: generateBookingRef() });
    setModal('add');
  };
  const openEdit = b => {
    const booked = b.bookedAt;
    let bookedStr = new Date().toISOString().slice(0, 16);
    try {
      const d = booked?.toDate ? booked.toDate() : new Date(booked);
      if (!Number.isNaN(d.getTime())) bookedStr = d.toISOString().slice(0, 16);
    } catch (_) {}

    setForm({
      bookingRef: b.bookingRef || b.id,
      type: b.type || 'flight',
      status: b.status || 'pending',
      title: b.title || b.eventTitle || '',
      subtitle: b.subtitle || b.flightNumber || '',
      userId: b.userId || '',
      totalPrice: String(b.totalPrice ?? ''),
      bookedAt: bookedStr,
      paymentMethod: b.paymentMethod || 'card',
    });
    setModal(b);
  };
  const openView = b => setModal({ view: b });
  const close = () => setModal(null);
  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const save = async () => {
    if (!form.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    setSaving(true);
    try {
      const bookedAt = new Date(form.bookedAt).toISOString();
      const payload = {
        bookingRef: form.bookingRef.trim().toUpperCase(),
        type: form.type,
        status: form.status,
        title: form.title.trim(),
        subtitle: form.subtitle.trim(),
        userId: form.userId.trim(),
        totalPrice: Number(form.totalPrice) || 0,
        bookedAt,
        paymentMethod: form.paymentMethod,
      };

      if (modal === 'add') {
        await addBooking(payload);
        toast.success('Booking created');
      } else {
        await updateBooking(modal.id, payload);
        toast.success('Booking updated');
      }
      close();
      load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
    setSaving(false);
  };

  const remove = async b => {
    const id = b.id || b.bookingRef;
    if (!window.confirm(`Delete booking ${id}? This cannot be undone.`)) return;
    try {
      await deleteBooking(id);
      toast.success('Booking deleted');
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const setStatus = async (b, status) => {
    const id = b.id || b.bookingRef;
    try {
      if (status === 'cancelled') await cancelBooking(id);
      else if (status === 'confirmed') await confirmBooking(id);
      else await updateBooking(id, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const formatDate = raw => {
    if (!raw) return '—';
    try {
      const d = raw.toDate ? raw.toDate() : new Date(raw);
      return d.toLocaleString();
    } catch {
      return String(raw).slice(0, 10);
    }
  };

  const filtered = bookings.filter(b => {
    const matchSearch = `${b.bookingRef} ${b.title} ${b.userId} ${b.id}`
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchFilter =
      filter === 'all' || b.type === filter || b.status === filter;
    return matchSearch && matchFilter;
  });

  const totalRevenue = filtered.reduce((s, b) => s + (b.totalPrice || 0), 0);
  const totalProfit = totalRevenue * PROFIT_RATE;
  const isView = modal && modal.view;

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-white font-bold text-xl">Bookings</h2>
          <p className="text-[#546E7A] text-sm">{bookings.length} bookings · create, edit, delete</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex gap-2 text-sm">
            <div className="bg-[#1A2235] border border-[#1E2D45] rounded-xl px-4 py-2">
              <span className="text-[#546E7A]">Revenue: </span>
              <span className="text-[#D4AF37] font-bold">
                ${totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
            <div className="bg-[#1A2235] border border-[#1E2D45] rounded-xl px-4 py-2">
              <span className="text-[#546E7A]">Profit: </span>
              <span className="text-green-400 font-bold">
                ${totalProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </span>
            </div>
          </div>
          <button
            onClick={openAdd}
            className="flex items-center gap-2 px-4 py-2.5 bg-[#2D5BE3] hover:bg-[#1A3C8F]
              text-white rounded-xl text-sm font-semibold transition-colors"
          >
            <Plus size={16} /> Add Booking
          </button>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        {['all', 'flight', 'event', 'confirmed', 'cancelled', 'pending'].map(f => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-colors capitalize
              ${filter === f ? 'bg-[#2D5BE3] text-white' : 'bg-[#1A2235] border border-[#1E2D45] text-[#546E7A] hover:text-white'}`}
          >
            {f}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#546E7A]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search ref, title, user..."
            className="pl-8 pr-3 py-1.5 bg-[#1A2235] border border-[#1E2D45] rounded-xl
              text-white text-xs placeholder-[#546E7A] focus:outline-none focus:border-[#2D5BE3] w-52"
          />
        </div>
      </div>

      <div className="bg-[#1A2235] border border-[#1E2D45] rounded-2xl overflow-x-auto">
        <table className="w-full min-w-[960px]">
          <thead>
            <tr className="border-b border-[#1E2D45]">
              {['Ref', 'Type', 'Details', 'User', 'Date', 'Amount', 'Status', 'Actions'].map(h => (
                <th key={h} className="text-left text-xs text-[#546E7A] font-semibold px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center py-12 text-[#546E7A]">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-[#546E7A]">No bookings found</td></tr>
            ) : filtered.map((b, i) => (
              <tr key={b.id || i} className="border-b border-[#1E2D45] hover:bg-white/[0.02]">
                <td className="px-4 py-3">
                  <span className="text-white font-mono text-xs font-bold">{b.bookingRef || b.id}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    {b.type === 'flight'
                      ? <Plane size={14} className="text-[#2D5BE3]" />
                      : <Calendar size={14} className="text-[#9B59B6]" />}
                    <span className="text-xs text-[#B0BEC5] capitalize">{b.type || '—'}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-white text-sm font-medium">{b.title || b.eventTitle || '—'}</div>
                  <div className="text-[#546E7A] text-xs">{b.subtitle || b.flightNumber || ''}</div>
                </td>
                <td className="px-4 py-3 text-[#B0BEC5] text-xs font-mono">
                  {(b.userId || '—').slice(0, 10)}{(b.userId?.length > 10) ? '…' : ''}
                </td>
                <td className="px-4 py-3 text-[#B0BEC5] text-xs">{formatDate(b.bookedAt)}</td>
                <td className="px-4 py-3">
                  <div className="text-[#D4AF37] font-semibold text-sm">
                    ${(b.totalPrice || 0).toFixed(0)}
                  </div>
                  <div className="text-green-400 text-xs">
                    +${((b.totalPrice || 0) * PROFIT_RATE).toFixed(0)}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold capitalize ${statusColor(b.status)}`}>
                    {b.status || 'pending'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5">
                    <button onClick={() => openView(b)} title="View"
                      className="w-8 h-8 rounded-lg bg-[#1E2D45] hover:bg-white/10 flex items-center justify-center text-[#B0BEC5]">
                      <Eye size={14} />
                    </button>
                    <button onClick={() => openEdit(b)} title="Edit"
                      className="w-8 h-8 rounded-lg bg-[#2D5BE3]/10 flex items-center justify-center text-[#2D5BE3]">
                      <Pencil size={14} />
                    </button>
                    {b.status !== 'confirmed' && (
                      <button onClick={() => setStatus(b, 'confirmed')} title="Confirm"
                        className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center text-green-400">
                        <CheckCircle size={14} />
                      </button>
                    )}
                    {b.status !== 'cancelled' && (
                      <button onClick={() => setStatus(b, 'cancelled')} title="Cancel"
                        className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center text-orange-400">
                        <XCircle size={14} />
                      </button>
                    )}
                    <button onClick={() => remove(b)} title="Delete"
                      className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center text-red-400">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && isView && (
        <AdminModal title={`Booking ${modal.view.bookingRef || modal.view.id}`} onClose={close} wide>
          <div className="space-y-3">
            {[
              ['Reference', modal.view.bookingRef || modal.view.id],
              ['Type', modal.view.type],
              ['Status', modal.view.status],
              ['Title', modal.view.title || modal.view.eventTitle],
              ['Subtitle', modal.view.subtitle || modal.view.flightNumber],
              ['User ID', modal.view.userId],
              ['Amount', `$${modal.view.totalPrice || 0}`],
              ['Payment', modal.view.paymentMethod],
              ['Booked', formatDate(modal.view.bookedAt)],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between py-2 border-b border-[#1E2D45]/50">
                <span className="text-[#546E7A] text-sm">{k}</span>
                <span className="text-white text-sm text-right max-w-[60%] break-all">{v || '—'}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-6">
            <button onClick={() => { close(); openEdit(modal.view); }}
              className="flex-1 py-2.5 bg-[#2D5BE3] text-white rounded-xl text-sm font-semibold">
              Edit Booking
            </button>
            <button onClick={close}
              className="flex-1 py-2.5 bg-[#1A2235] border border-[#1E2D45] text-[#B0BEC5] rounded-xl text-sm">
              Close
            </button>
          </div>
        </AdminModal>
      )}

      {modal && !isView && (
        <AdminModal
          title={modal === 'add' ? 'Add Booking' : 'Edit Booking'}
          onClose={close}
          wide
          footer={
            <ModalFooter
              onCancel={close}
              onSave={save}
              saving={saving}
              saveLabel={modal === 'add' ? 'Create Booking' : 'Save Changes'}
            />
          }
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FormInput
              label="Booking Reference"
              value={form.bookingRef}
              onChange={v => f('bookingRef', v)}
              required
              className="font-mono"
            />
            {modal === 'add' && (
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={() => f('bookingRef', generateBookingRef())}
                  className="w-full py-2 bg-[#1A2235] border border-[#1E2D45] rounded-xl text-[#2D5BE3] text-xs font-semibold hover:bg-white/5"
                >
                  Regenerate Ref
                </button>
              </div>
            )}
            <FormSelect
              label="Type"
              value={form.type}
              onChange={v => f('type', v)}
              options={[
                { value: 'flight', label: 'Flight' },
                { value: 'event', label: 'Event' },
              ]}
            />
            <FormSelect
              label="Status"
              value={form.status}
              onChange={v => f('status', v)}
              options={[
                { value: 'confirmed', label: 'Confirmed' },
                { value: 'pending', label: 'Pending' },
                { value: 'cancelled', label: 'Cancelled' },
              ]}
            />
            <FormInput label="Title" value={form.title} onChange={v => f('title', v)} required className="sm:col-span-2" />
            <FormInput label="Subtitle / Flight #" value={form.subtitle} onChange={v => f('subtitle', v)} />
            <FormInput label="User ID (Firebase UID)" value={form.userId} onChange={v => f('userId', v)} />
            <FormInput label="Total Price ($)" value={form.totalPrice} onChange={v => f('totalPrice', v)} type="number" />
            <FormInput label="Booked At" value={form.bookedAt} onChange={v => f('bookedAt', v)} type="datetime-local" />
            <FormSelect
              label="Payment Method"
              value={form.paymentMethod}
              onChange={v => f('paymentMethod', v)}
              options={[
                { value: 'card', label: 'Card' },
                { value: 'wallet', label: 'Wallet' },
                { value: 'admin', label: 'Admin' },
              ]}
            />
          </div>
        </AdminModal>
      )}
    </div>
  );
}

function statusColor(s) {
  switch (s) {
    case 'confirmed': return 'bg-green-500/10 text-green-400';
    case 'cancelled': return 'bg-red-500/10 text-red-400';
    default: return 'bg-yellow-500/10 text-yellow-400';
  }
}
